import React, { ReactNode } from 'react';
import { DragSource } from 'react-dnd';
import Icon from 'flow/components/ui-components/Icon';
import { ProfileKey } from 'flow/types/EntityKeys';
import { Profile } from 'flow/types/Profile';
import { TypedMap } from 'flow/types/TypedMap';
import { getBgColorFromKey_TW } from 'flow/utils';
import { twMerge } from 'tailwind-merge';

interface Props {
    draggable: boolean;
    setProfileKeyBeingDragged: (profileKey: ProfileKey) => void;
    profile: TypedMap<Profile>;
    connectDragSource: (element: ReactNode) => ReactNode;
    isDragging: boolean;
}

const ProfileThumbnail = ({
    profile,
    draggable,
    connectDragSource,
    isDragging,
}: Props) => {
    const profileThumb = (
        <div
            className={twMerge(
                'flex min-w-2 translate-0',
                draggable && 'cursor-grab',
                isDragging && 'cursor-grabbing opacity-25',
            )}
            key={`profile-thumbnail${profile.get('key')}${isDragging}`} // Change key while dragging to prevent chrome drag effect
        >
            <div
                className={twMerge(
                    'flex flex-shrink-0 items-center',
                    getBgColorFromKey_TW(profile.get('key')),
                )}
            >
                {draggable ? <Icon iconKey="dragHandle" /> : null}
            </div>
            <div
                className={twMerge(
                    'p-1 pe-4 text-sm text-black',
                    `${getBgColorFromKey_TW(profile.get('key'))}/50`,
                )}
            >
                {profile.get('description')}
            </div>
        </div>
    );

    return draggable ? connectDragSource(profileThumb) : profileThumb;
};

const profileReactions = {
    beginDrag: ({ profile, setProfileKeyBeingDragged }: Props) => {
        const profileKey = profile.get('key');
        setProfileKeyBeingDragged(profileKey);
        return { profileKey, dropEffect: 'none', effectAllowed: 'none' };
    },
};

function collect(
    connect: { dragSource: () => (element: ReactNode) => ReactNode },
    monitor: { isDragging: () => boolean },
) {
    return {
        connectDragSource: connect.dragSource(),
        isDragging: monitor.isDragging(),
    };
}

// IMPORTANT NOTE: The 'event' string below is a magical string
// that connects our component (the ProfileThumbnail) as a candidate
// to be dropped in the DNDBigCalendar.
// This origin of the string can be found in "backgroundWrapper.js"
// of the react BigCalendar dragAndDrop addon source code.
// An alternative may (not tested) to wrap the component in
// "DraggableEventWrapper" of react BigCalendar dragAndDrop.
export default DragSource(
    'event',
    profileReactions,
    collect,
)(React.memo(ProfileThumbnail));
